/**
 * glTF / KTX2 / meshopt loader configuration (P1-T05).
 *
 * The babylonjs UMD core has NO glTF loader; `babylonjs-loaders` is imported
 * here for its side effect (it self-registers the glTF2 loader and the
 * KHR_texture_basisu / EXT_meshopt_compression extensions on SceneLoader).
 *
 * All decoder binaries are vendored under /decoders (apps/client/public/
 * decoders) from the pinned @babylonjs/ktx2decoder@9.29.0 npm package — the
 * game never depends on cdn.babylonjs.com at runtime.
 */
import { KhronosTextureContainer2, MeshoptCompression, Tools } from 'babylonjs';
import 'babylonjs-loaders';

let done = false;

/** Point Babylon's KTX2 + meshopt machinery at the vendored decoders. Idempotent. */
export function configureAssetLoader(): void {
  if (done) return;
  done = true;

  const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost:5173';
  const abs = (p: string): string => new URL(p, origin + '/').href;

  // meshopt (EXT_meshopt_compression): UMD decoder script, wasm inlined
  MeshoptCompression.Configuration.decoder.url = abs('/decoders/meshopt_decoder.js');

  // KTX2 (KHR_texture_basisu): decoder module + every transcoder wasm.
  // ETC1S (what assets:optimise emits) transcodes via MSCTranscoder; the
  // lite UASTC transcoders and zstd stay local for the same build path.
  const K = KhronosTextureContainer2;
  const t = '/decoders/ktx2Transcoders/1/';
  K.URLConfig.jsDecoderModule = abs('/decoders/babylon.ktx2Decoder.js');
  K.URLConfig.jsMSCTranscoder = abs(t + 'msc_basis_transcoder.js');
  K.URLConfig.wasmMSCTranscoder = abs(t + 'msc_basis_transcoder.wasm');
  K.URLConfig.wasmUASTCToASTC = abs(t + 'uastc_astc.wasm');
  K.URLConfig.wasmUASTCToBC7 = abs(t + 'uastc_bc7.wasm');
  K.URLConfig.wasmUASTCToRGBA_UNORM = abs(t + 'uastc_rgba8_unorm_v2.wasm');
  K.URLConfig.wasmUASTCToRGBA_SRGB = abs(t + 'uastc_rgba8_srgb_v2.wasm');
  K.URLConfig.wasmUASTCToR8_UNORM = abs(t + 'uastc_r8_unorm.wasm');
  K.URLConfig.wasmUASTCToRG8_UNORM = abs(t + 'uastc_rg8_unorm.wasm');
  K.URLConfig.wasmZSTDDecoder = abs('/decoders/zstddec.wasm');

  // CORS preflight so worker-side fetches of assets.sintim.io fail fast/smart
  Tools.CorsBehavior = 'UseCors';
}
